/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React, { useState } from 'react';
import ChipInput from 'material-ui-chip-input';
import { Trans, useTranslation } from 'react-i18next';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { uiSchemaProperties } from '../../../shared/enums/uiSchemaProperties';
import AssistiveText from './AssistiveText';
import { text } from '../../../shared/validations/standardChecks';
import Tooltip from '../../common/Tooltip';

const useStyles = makeStyles(theme => ({
  tooltip: {
    position: 'absolute',
    marginLeft: '0.75rem',
    marginTop: '1rem',
  },
  chipInput: {
    '& .MuiChip-clickable': {
      overflow: 'auto',
    },
  },
  ipcCodes: {
    [theme.breakpoints.up('sm')]: {
      width: '95%',
    },
    [theme.breakpoints.up('md')]: {
      width: 'inherit',
    },
  },
}));

const ChipsField = ({ formData, schema, uiSchema, disabled, rawErrors, label, onChange }) => {
  const { t } = useTranslation();
  const [errorMessage, setErrorMessage] = useState(null);

  const classes = useStyles();
  const isDisabled = uiSchema?.['ui:disabled'] || disabled;
  const displayLabel = uiSchema?.[uiSchemaProperties.UI_TITLE]
    ? (uiSchema?.title && t(uiSchema?.config?.title)) || label || schema.title
    : false;

  const [ipcCodes, setIpcCodes] = useState(formData || []);
  const tooltip = schema?.config?.tooltip ?? uiSchema?.[uiSchemaProperties.UI_TOOLTIP];

  const handleAddChip = (chip) => {
    setErrorMessage(null);
    const lengthValidation = text(0, schema?.items?.maxLength)(chip);
    if (!lengthValidation) {
      setErrorMessage(lengthValidation);
      return;
    }
    const patternValidation = schema?.items?.pattern != null ? new RegExp(schema?.items?.pattern).test(chip) : false;
    if (!patternValidation) {
      setErrorMessage(t(uiSchema?.['ui:validationPatternMessage']));
      return;
    }

    ipcCodes.push(chip);
    setIpcCodes(ipcCodes);
    onChange(ipcCodes);
  };

  const handleDeleteChip = (chip, index) => {
    ipcCodes.splice(index, 1);
    setIpcCodes(ipcCodes);
    onChange(ipcCodes);
  };

  return (
    <div className={classes.ipcCodes}>
      <div>
        <ChipInput
          className={classes.chipInput}
          data-testid="chip-input"
          blurBehavior="add"
          value={ipcCodes}
          onAdd={chip => handleAddChip(chip)}
          onDelete={(chip, index) => handleDeleteChip(chip, index)}
          disabled={isDisabled}
          error={rawErrors.length > 0}
          label={displayLabel}
          variant="outlined"
          fullWidth
          fullWidthInput
          InputProps={{
            notched: true,
            label: displayLabel,
          }}
        />
        {tooltip && <Tooltip className={classes.tooltip} title={<Trans i18nKey={tooltip} />} />}
      </div>
      {errorMessage && <AssistiveText severity="error">{errorMessage}</AssistiveText>}
    </div>
  );
};

ChipsField.defaultProps = {
  rawErrors: [],
  uiSchema: {},
  schema: {},
};
export default ChipsField;
